import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import icon from 'flarum/common/helpers/icon';

/**
 * The poll attached to a story, shown over the content while it plays.
 *
 * Before voting the options are plain buttons; afterwards each turns into a
 * bar showing its share, with the viewer's own answer ticked. Results appear
 * immediately because the feedback is the point — a poll that reveals nothing
 * gives the viewer no reason to answer the next one.
 *
 * Props:
 *   story  – the story being shown
 *   onvote – callback(optionIndex)
 */
export default class StoryPoll extends Component {
  oninit(vnode) {
    super.oninit(vnode);

    this.sending = null;
  }

  view() {
    const poll = this.attrs.story.poll;

    if (!poll) return null;

    const answered = poll.myAnswer !== null && poll.myAnswer !== undefined;

    return (
      <div
        className="StoryPollBox"
        // The viewer's tap areas advance the story; a vote must not also skip.
        onclick={(e) => e.stopPropagation()}
        onmousedown={(e) => e.stopPropagation()}
        ontouchstart={(e) => e.stopPropagation()}
      >
        <div className="StoryPollBox-question">
          {icon('fas fa-poll', { className: 'StoryPollBox-icon' })}
          {poll.question}
        </div>

        {/* Wrapped: the options carry keys and the question and total beside
            them do not, and Mithril rejects keyed and unkeyed siblings under
            one parent. The wrapper gives the keyed list a parent of its own. */}
        <div className="StoryPollBox-options">
          {poll.options.map((option, i) =>
            answered ? this.result(poll, option, i) : this.choice(option, i)
          )}
        </div>

        <div className="StoryPollBox-total">
          {poll.totalVotes > 0
            ? app.translator.trans('happydigital-stories.forum.poll_votes', {
                count: poll.totalVotes,
              })
            : app.translator.trans('happydigital-stories.forum.poll_no_votes')}
        </div>
      </div>
    );
  }

  /** An option before the viewer has answered. */
  choice(option, i) {
    return (
      <button
        className="StoryPollBox-choice"
        type="button"
        disabled={this.sending !== null}
        onclick={() => this.vote(i)}
        key={i}
      >
        {this.sending === i ? icon('fas fa-spinner fa-spin') : null} {option}
      </button>
    );
  }

  /** An option after answering: a share bar, ticked when it was the answer. */
  result(poll, option, i) {
    const votes = poll.votes[i] || 0;
    const share = poll.totalVotes > 0 ? Math.round((votes / poll.totalVotes) * 100) : 0;
    const mine = poll.myAnswer === i;

    return (
      <button
        className={'StoryPollBox-result' + (mine ? ' is-mine' : '')}
        type="button"
        // Answering again is allowed: the server updates the existing vote.
        onclick={() => this.vote(i)}
        key={i}
      >
        <span className="StoryPollBox-bar" style={{ width: share + '%' }} />
        <span className="StoryPollBox-label">
          {option}
          {mine ? icon('fas fa-check', { className: 'StoryPollBox-tick' }) : null}
        </span>
        <span className="StoryPollBox-share">%{share}</span>
      </button>
    );
  }

  vote(index) {
    const story = this.attrs.story;

    if (this.sending !== null || story.poll.myAnswer === index) return;

    this.sending = index;
    m.redraw();

    app
      .request({
        method: 'POST',
        url: '/api/happydigital/stories/' + story.id + '/poll-vote',
        body: { option: index },
      })
      .then((result) => {
        this.sending = null;

        // Trust the server's tally rather than incrementing locally: someone
        // else may have voted while this request was in flight.
        story.poll.votes = result.pollVotes;
        story.poll.myAnswer = result.pollMyAnswer;
        story.poll.totalVotes = result.pollVotes.reduce((sum, n) => sum + n, 0);

        if (this.attrs.onvote) this.attrs.onvote(index);

        m.redraw();
      })
      .catch(() => {
        this.sending = null;
        m.redraw();
      });
  }
}
